import React, { useEffect, useState, useRef, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./styles.module.css";
import Toggle from "../../../components/ui/toggle";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../../components/button-v1";
import { useMutation } from "@tanstack/react-query";
import { createOrganization } from "../../../services/admin/organization";
import { validateTenoviCreds } from "../../../services/tenovi";
import toast from "react-hot-toast";
import { timeZones, states } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import Input from "../../../components/ui/input";
import { getUserRoleSlug } from "../../../utils/authorization";

const defaultValues = {
  name: "",
  invoice_recipient_email: "",
  account_biller_contact_email: "",
  tenovi_client_name: "",
  tenovi_api_key: "",
  tenovi_webhook_header: "",
  timezone: "",
  city: "",
  account_state: "",
  country: "",
  acccount_phone: "",
  postal_code: "",
  address_line_1: "",
  is_ccm: true,
  is_rpm: false,
  vital_sms_alert: false,
  location_name: "",
  location_email: "",
  location_phone_number: "",
  location_fax_number: "",
  location_address: "",
  location_timezone: "",
  location_notes: "",
  staff_first_name: "",
  staff_last_name: "",
  staff_email: "",
  staff_phone_number: "",
  staff_fax_number: "",
};

const step1Schema = z
  .object({
    name: z
      .string()
      .min(1, "Hospital name is required")
      .regex(
        /^[a-zA-Z0-9\s]+$/,
        "Hospital name cannot contain special characters"
      ),
    invoice_recipient_email: z
      .string()
      .email("Invalid email")
      .optional()
      .or(z.literal("")),
    account_biller_contact_email: z.string().optional(),
    account_phone: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    timezone: z.string().min(1, "Timezone is required"),
    city: z.string().min(1, "City name is required"),
    account_state: z.string().min(1, "State is required"),
    country: z.string().optional(),
    postal_code: z.string().optional(),
    address_line_1: z.string().min(1, "Address line 1 is required"),
    tenovi_client_name: z.string().optional(),
    tenovi_api_key: z.string().optional(),
    tenovi_webhook_header: z.string().nullable(),
    is_ccm: z.boolean(),
    is_rpm: z.boolean(),
  })
  .refine((data) => data.is_ccm || data.is_rpm, {
    message: "At least one program (CCM or RPM) must be selected",
    path: ["programs"],
  });

// const step2Schema = z.object({
//   tenovi_client_name: z.string().min(1, "Tenovi client name is required"),
//   tenovi_api_key: z.string().min(1, "API key is required"),
// });

const step2Schema = z.object({
  location_name: z.string().min(1, "Location name is required"),
  location_email: z.string().email("Email is required"),
  location_phone_number: z
    .string()
    .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
      message: "Invalid phone number",
    })
    .transform((val) => val.replace(/\D/g, ""))
    .refine(
      (val) => val.length === 10 || (val.length === 11 && val.startsWith("1")),
      { message: "Invalid phone number" }
    )
    .transform((val) => {
      return val.length === 11 ? val.slice(1) : val;
    }),
  location_fax_number: z.string().min(1, "Fax number is required"),
  location_address: z.string().min(1, "Address is required"),
  location_timezone: z.string().min(1, "Timezone is required"),
  location_notes: z.string().optional(),
});

const step3Schema = z
  .object({
    staff_first_name: z.string().min(1, "First name is required"),
    staff_last_name: z.string().min(1, "Last name is required"),
    staff_email: z.string().email("Email is required"),
    staff_phone_number: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    staff_password: z.string().min(1, "Password is required"),
    staff_confirm_password: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.staff_password === data.staff_confirm_password, {
    message: "Passwords do not match",
    path: ["staff_confirm_password"],
  });

const schema = z
  .object({
    name: z
      .string()
      .min(1, "Hospital name is required")
      .regex(
        /^[a-zA-Z0-9\s]+$/,
        "Hospital name cannot contain special characters"
      ),
    invoice_recipient_email: z.string().email("Email is required").optional(),
    account_biller_contact_email: z
      .string()
      .email("Email is required")
      .optional(),
    tenovi_client_name: z.string().optional(),
    tenovi_api_key: z.string().optional(),
    tenovi_webhook_header: z.string().nullable(),
    timezone: z.string().min(1, "Timezone is required"),
    city: z.string().min(1, "City name is required"),
    acccount_state: z.string().min(1, "State is required"),
    acccount_phone: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    country: z.string().optional(),
    postal_code: z.string().optional(),
    address_line_1: z.string().min(1, "Address line 1 is required"),
    is_ccm: z.boolean(),
    is_rpm: z.boolean(),
    location_name: z.string().min(1, "Location name is required"),
    location_email: z.string().email("Email is required"),
    location_phone_number: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    location_fax_number: z.string().min(1, "Fax number is required"),
    location_address: z.string().min(1, "Address is required"),
    location_timezone: z.string().min(1, "Timezone is required"),
    location_notes: z.string().optional(),
    staff_first_name: z.string().min(1, "First name is required"),
    staff_last_name: z.string().min(1, "Last name is required"),
    staff_email: z.string().email("Email is required"),
    staff_phone_number: z
      .string()
      .refine((val) => /^[0-9+\-\s()]+$/.test(val), {
        message: "Invalid phone number",
      })
      .transform((val) => val.replace(/\D/g, ""))
      .refine(
        (val) =>
          val.length === 10 || (val.length === 11 && val.startsWith("1")),
        { message: "Invalid phone number" }
      )
      .transform((val) => {
        return val.length === 11 ? val.slice(1) : val;
      }),
    staff_pass: z.string().min(1, "Password is required"),
    staff_confirm_pass: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.staff_pass === data.staff_confirm_pass, {
    message: "Passwords do not match",
    key: "staff_confirm_password",
  });

const AddOrganization = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [allFormData, setAllFormData] = useState(defaultValues);
  const formUpdateTimeoutRef = useRef(null);

  // Watch specific fields and update allFormData with debounce
  const onFormChange = useCallback((data) => {
    if (formUpdateTimeoutRef.current) {
      clearTimeout(formUpdateTimeoutRef.current);
    }

    formUpdateTimeoutRef.current = setTimeout(() => {
      setAllFormData((prev) => ({
        ...prev,
        ...data,
      }));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (formUpdateTimeoutRef.current) {
        clearTimeout(formUpdateTimeoutRef.current);
      }
    };
  }, []);

  const createOrganizationMutation = useMutation(
    (data) => createOrganization(data),
    {
      onSuccess: (data) => {
        toast.success("Organization created successfully");
        setAllFormData(defaultValues);
        setStep(1);
        reset();
        navigate("/admin/dashboard");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || "Could not add organization"
        );
      },
    }
  );

  const validateTenoviCredsMutation = useMutation(
    (data) => validateTenoviCreds(data),
    {
      onSuccess: () => {
        toast.success("Tenovi Info is valid");
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error ?? "Invalid Tenovi Info");
      },
    }
  );

  const getStepSchema = (currentStep) => {
    switch (currentStep) {
      case 1:
        return step1Schema;
      // case 2:
      //   return step2Schema;
      case 2:
        return step2Schema;
      case 3:
        return step3Schema;
      default:
        return schema;
    }
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: allFormData,
    resolver: async (data, context, options) => {
      const stepSchema = getStepSchema(step);
      return zodResolver(stepSchema)(data, context, options);
    },
    mode: "onChange",
  });

  useEffect(() => {
    reset(allFormData);
  }, [step, reset]);

  useEffect(() => {
    if (step === 2) {
      setValue("location_name", allFormData.name);
    }
  }, [step]);

  const onSubmit = (data) => {
    const mergedData = {
      ...allFormData,
      ...data,
    };

    if (step < 3) {
      if (step === 1) {
        if (mergedData.is_rpm) {
          const clientName = mergedData.tenovi_client_name?.trim();
          const apiKey = mergedData.tenovi_api_key?.trim();
          const tenovi_webhook_header =
            mergedData.tenovi_webhook_header.trim();

          if (clientName || apiKey || tenovi_webhook_header) {
            const tenoviInfo = {
              client_name: clientName,
              api_key: apiKey,
              tenovi_webhook_header,
            };

            validateTenoviCredsMutation.mutate(tenoviInfo, {
              onSuccess: () => {
                setStep(step + 1);
              },
              onError: () => {},
            });
          } else {
            setStep(step + 1);
          }
        } else {
          setStep(step + 1);
        }
      } else {
        setStep(step + 1);
      }
    } else {
      const user = {
        first_name: mergedData.staff_first_name,
        last_name: mergedData.staff_last_name,
        email: mergedData.staff_email,
        primary_phone: mergedData.staff_phone_number,
        password: mergedData.staff_password,
      };

      const location = {
        name: mergedData.location_name,
        email: mergedData.location_email,
        phone_number: mergedData.location_phone_number,
        fax_number: mergedData.location_fax_number,
        address_line_1: mergedData.location_address,
        location_notes: mergedData.location_notes,
        timezone: mergedData.location_timezone,
      };

      const body = {
        name: mergedData.name,
        invoice_recipient_email: mergedData.invoice_recipient_email,
        account_biller_email: mergedData.account_biller_contact_email,
        phone_number: mergedData.account_phone,
        tenovi_client: mergedData.tenovi_client_name,
        tenovi_api_key: mergedData.tenovi_api_key,
        tenovi_webhook_header: mergedData.tenovi_webhook_header,
        timezone: mergedData.timezone,
        city: mergedData.city,
        state: mergedData.account_state,
        country: mergedData.country,
        postal_code: mergedData.postal_code,
        address_line_1: mergedData.address_line_1,
        is_ccm: mergedData.is_ccm,
        is_rpm: mergedData.is_rpm,
        vital_sms_alert: mergedData.vital_sms_alert,
        user,
        location,
      };

      // Remove keys with null, undefined, or empty string values
      const filteredBody = Object.fromEntries(
        Object.entries(body).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      // Here you have access to all steps data in mergedData
      createOrganizationMutation.mutate(filteredBody);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  }; // Watch specific fields that we want to sync
  const formValues = watch();

  useEffect(() => {
    // Create a new object with only changed values
    const changedValues = {};
    let hasChanges = false;

    Object.keys(formValues).forEach((key) => {
      if (formValues[key] !== allFormData[key]) {
        changedValues[key] = formValues[key];
        hasChanges = true;
      }
    });

    // Only update if there are actual changes
    if (hasChanges) {
      setAllFormData((prev) => ({
        ...prev,
        ...changedValues,
      }));
    }
  }, [formValues, allFormData]);

  const is_ccm = watch("is_ccm");
  const is_rpm = watch("is_rpm");
  const vital_sms_alert = watch("vital_sms_alert");
  const timezone = watch("timezone");
  const account_state = watch("account_state");
  const location_timezone = watch("location_timezone");
  const roleSlug = getUserRoleSlug();
  const showVitalSmsToggle = [
    "toca_admin",
    "toca_care_manager",
    "toca_super_admin",
  ].includes(roleSlug);

  return (
    <div className={styles.staff_page}>
      <div className={styles.staff_page_container}>
        <div className={styles.patient_wrapper}>
          <div className={styles.top}>
            <p className={styles.heading}>Add Organization</p>
          </div>

          {/* <div className={styles.table_container}> */}
          <div className={styles.form_container}>
            {" "}
            <div className={styles.steps_container}>
              {" "}
              <div
                onClick={() => {
                  if (step > 1) {
                    setStep(1);
                  }
                }}
                className={`${styles.step} ${step >= 1 ? styles.active : ""}`}
              >
                <span className={styles.step_number}>1</span>
                <span className={styles.step_text}>Account Information</span>
              </div>
              <div
                onClick={() => {
                  if (step === 1) {
                    document.getElementById("next-btn").click();
                  } else if (step > 2) {
                    setStep(2);
                  }
                }}
                className={`${styles.step} ${step >= 2 ? styles.active : ""}`}
              >
                <span className={styles.step_number}>2</span>
                <span className={styles.step_text}>Location Information</span>
              </div>
              <div
                onClick={() => {
                  if (step < 3) {
                    document.getElementById("next-btn").click();
                  }
                }}
                className={`${styles.step} ${step >= 3 ? styles.active : ""}`}
              >
                <span className={styles.step_number}>3</span>
                <span className={styles.step_text}>Staff Information</span>
              </div>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              {step === 1 && (
                <div className={styles.form_step}>
                  <h3 className={styles.form_title}>Account Information</h3>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Hospital Name</label>
                      <input type="text" {...register("name")} />
                      {errors.name && (
                        <span className={styles.error}>
                          {errors.name.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Invoice Recipient Email</label>
                      <input
                        type="email"
                        {...register("invoice_recipient_email")}
                      />
                      {errors.invoice_recipient_email && (
                        <span className={styles.error}>
                          {errors.invoice_recipient_email.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Account Biller Email</label>
                      <input
                        type="email"
                        {...register("account_biller_contact_email")}
                      />
                      {errors.account_biller_contact_email && (
                        <span className={styles.error}>
                          {errors.account_biller_contact_email.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Phone</label>
                      <Controller
                        name="account_phone"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            mask={true}
                            maskPattern="(999) 999-9999"
                            name={field.name}
                            value={field.value || ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            className="no-arrows"
                          />
                        )}
                      />
                      {errors.account_phone && (
                        <span className={styles.error}>
                          {errors.account_phone.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>City</label>
                      <input type="text" {...register("city")} />
                      {errors.city && (
                        <span className={styles.error}>
                          {errors.city.message}
                        </span>
                      )}
                    </div>

                    <div className={styles.form_group}>
                      <label>State</label>
                      <Select
                        onChange={(value) => {
                          setValue("account_state", value);
                          clearErrors("account_state");
                        }}
                        value={account_state}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select state">
                            {account_state ?? "Select state"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {states.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.account_state && (
                        <span className={styles.error}>
                          {errors.account_state.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Country</label>
                      <input type="text" {...register("country")} />
                      {errors.country && (
                        <span className={styles.error}>
                          {errors.country.message}
                        </span>
                      )}
                    </div>

                    <div className={styles.form_group}>
                      <label>Postal Code</label>
                      <input type="number" {...register("postal_code")} />
                      {errors.postal_code && (
                        <span className={styles.error}>
                          {errors.postal_code.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Address</label>
                      <input type="text" {...register("address_line_1")} />
                      {errors.address_line_1 && (
                        <span className={styles.error}>
                          {errors.address_line_1.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Timezone</label>

                      <Select
                        onChange={(value) => {
                          setValue("timezone", value);
                          clearErrors("timezone");
                        }}
                        value={timezone}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timezone">
                            {timeZones.find((tz) => tz.value === timezone)
                              ?.label ?? "Select timezone"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {timeZones.map((zone) => (
                            <SelectItem key={zone.value} value={zone.value}>
                              {zone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.timezone && (
                        <span className={styles.error}>
                          {errors.timezone.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Programs</label>
                      <div className={styles.programs_container}>
                        <Toggle
                          label="CCM"
                          checked={is_ccm}
                          disabled={!is_ccm && is_rpm}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setValue("is_ccm", newValue);
                            if (!newValue && !is_rpm) {
                              setValue("is_rpm", true);
                            }
                          }}
                        />
                        <Toggle
                          label="RPM"
                          checked={is_rpm}
                          disabled={!is_rpm && is_ccm}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setValue("is_rpm", newValue);
                            if (!newValue) {
                              // Clear Tenovi fields when RPM is disabled
                              setValue("tenovi_client_name", "");
                              setValue("tenovi_api_key", "");
                              setValue("tenovi_webhook_header", "");
                              if (!is_ccm) {
                                setValue("is_ccm", true);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    {showVitalSmsToggle && (
                      <div className={styles.form_group}>
                        <label>Vital SMS Alert</label>
                        <div className={styles.toggle_group}>
                          <Toggle
                            label=""
                            checked={vital_sms_alert}
                            onChange={(e) => {
                              setValue("vital_sms_alert", e.target.checked);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {is_rpm && (
                    <div className={styles.tenovi_section}>
                      <h3 className={styles.form_second_title}>
                        Tenovi Information
                      </h3>

                      <div className={styles.form_row}>
                        <div className={styles.form_group}>
                          <label>Tenovi Client Name</label>
                          <input
                            type="text"
                            {...register("tenovi_client_name")}
                          />
                          {errors.tenovi_client_name && (
                            <span className={styles.error}>
                              {errors.tenovi_client_name.message}
                            </span>
                          )}
                        </div>
                        <div className={styles.form_group}>
                          <label>Tenovi API Key</label>
                          <input type="text" {...register("tenovi_api_key")} />
                          {errors.tenovi_api_key && (
                            <span className={styles.error}>
                              {errors.tenovi_api_key.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.form_row}>
                        <div className={styles.form_group}>
                          <label>Tenovi Webhook Header</label>
                          <input
                            type="text"
                            {...register("tenovi_webhook_header")}
                          />
                          {errors.tenovi_webhook_header && (
                            <span className={styles.error}>
                              {errors.tenovi_webhook_header.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* {step === 2 && (
            <div className={styles.form_step}>
              <h3 className={styles.form_title}>Tenovi Information</h3>

              <div className={styles.form_row}>
                <div className={styles.form_group}>
                  <label>Tenovi Client Name</label>
                  <input
                    type="text"
                    {...register("tenovi_client_name", {
                      required: "Address is required",
                    })}
                  />
                  {errors.tenovi_client_name && (
                    <span className={styles.error}>
                      {errors.tenovi_client_name.message}
                    </span>
                  )}
                </div>
                <div className={styles.form_group}>
                  <label>Tenovi API Key</label>
                  <input
                    type="text"
                    {...register("tenovi_api_key", {
                      required: "API key is required",
                    })}
                  />
                  {errors.tenovi_api_key && (
                    <span className={styles.error}>
                      {errors.tenovi_api_key.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )} */}

              {step === 2 && (
                <div className={styles.form_step}>
                  <h3 className={styles.form_title}>Location Information</h3>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Name</label>
                      <input
                        type="text"
                        {...register("location_name", {
                          required: "Name is required",
                        })}
                      />
                      {errors.location_name && (
                        <span className={styles.error}>
                          {errors.location_name.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Email</label>
                      <input
                        type="text"
                        {...register("location_email", {
                          required: "email is required",
                        })}
                      />
                      {errors.location_email && (
                        <span className={styles.error}>
                          {errors.location_email.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Phone</label>
                      <Controller
                        name="location_phone_number"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            mask={true}
                            maskPattern="(999) 999-9999"
                            name={field.name}
                            value={field.value || ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                      {errors.location_phone_number && (
                        <span className={styles.error}>
                          {errors.location_phone_number.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Fax</label>
                      <input
                        type="number"
                        {...register("location_fax_number", {
                          required: "Fax is required",
                        })}
                      />
                      {errors.location_fax_number && (
                        <span className={styles.error}>
                          {errors.location_fax_number.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Address</label>
                      <input
                        type="text"
                        {...register("location_address", {
                          required: "Address is required",
                        })}
                      />
                      {errors.location_address && (
                        <span className={styles.error}>
                          {errors.location_address.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Timezone</label>

                      <Select
                        onChange={(value) => {
                          setValue("location_timezone", value);
                          clearErrors("location_timezone");
                        }}
                        value={location_timezone}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timezone">
                            {timeZones.find(
                              (tz) => tz.value === location_timezone
                            )?.label ?? "Select timezone"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {timeZones.map((zone) => (
                            <SelectItem key={zone.value} value={zone.value}>
                              {zone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.location_timezone && (
                        <span className={styles.error}>
                          {errors.location_timezone.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Notes</label>
                      <textarea
                        type="text"
                        {...register("location_notes", {})}
                      />
                      {errors.location_notes && (
                        <span className={styles.error}>
                          {errors.location_notes.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className={styles.form_step}>
                  <h3 className={styles.form_title}>Staff Information</h3>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>First Name</label>
                      <input
                        type="text"
                        {...register("staff_first_name", {
                          required: "First name is required",
                        })}
                      />
                      {errors.staff_first_name && (
                        <span className={styles.error}>
                          {errors.staff_first_name.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Last Name</label>
                      <input
                        type="text"
                        {...register("staff_last_name", {
                          required: "last name is required",
                        })}
                      />
                      {errors.staff_last_name && (
                        <span className={styles.error}>
                          {errors.staff_last_name.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Email</label>
                      <input
                        type="email"
                        {...register("staff_email", {
                          required: "Email is required",
                        })}
                      />
                      {errors.staff_email && (
                        <span className={styles.error}>
                          {errors.staff_email.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Phone</label>
                      <Controller
                        name="staff_phone_number"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            mask={true}
                            maskPattern="(999) 999-9999"
                            name={field.name}
                            value={field.value || ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                      {errors.staff_phone_number && (
                        <span className={styles.error}>
                          {errors.staff_phone_number.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.form_row}>
                    <div className={styles.form_group}>
                      <label>Password</label>
                      <input
                        type="text"
                        {...register("staff_password", {
                          required: "Address is required",
                        })}
                      />
                      {errors.staff_password && (
                        <span className={styles.error}>
                          {errors.staff_password.message}
                        </span>
                      )}
                    </div>
                    <div className={styles.form_group}>
                      <label>Confirm Password</label>
                      <input
                        type="text"
                        {...register("staff_confirm_password", {
                          required: "Address is required",
                        })}
                      />
                      {errors.staff_confirm_password && (
                        <span className={styles.error}>
                          {errors.staff_confirm_password.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.button_group}>
                {step > 1 && (
                  <Button type="button" onClick={handlePrevious}>
                    Previous
                  </Button>
                )}
                <Button
                  id="next-btn"
                  loading={createOrganizationMutation.isLoading}
                  type="submit"
                >
                  {step === 3 ? "Submit" : "Next"}
                </Button>
              </div>
            </form>
          </div>
          {/* </div> */}
        </div>
      </div>
    </div>
  );
};

export default AddOrganization;
